import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const ClientsSection = styled.section`
  background-color: ${props => props.theme.colors.dark};
  position: relative;
  overflow: hidden;
  padding: 6rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  text-align: center;
  margin-bottom: 1rem;

  .highlight {
    color: ${props => props.theme.colors.primary};
  }
`;

const SectionSubtitle = styled.p`
  color: ${props => props.theme.colors.gray};
  text-align: center;
  max-width: 600px;
  margin: 0 auto 3rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const MapWrapper = styled(motion.div)`
  position: relative;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  background: ${props => props.theme.colors.background};
  overflow: hidden;
  padding: 1rem;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  .geo {
    fill: #16191d;
    stroke: #24282e;
    stroke-width: 0.5;
    outline: none;
    transition: fill 0.3s ease;
  }

  .geo:hover {
    fill: #1d2127;
  }

  .marker-dot {
    fill: ${props => props.theme.colors.primary};
  }

  .marker-pulse {
    fill: none;
    stroke: ${props => props.theme.colors.primary};
    stroke-width: 1.5;
    opacity: 0.8;
    animation: markerPulse 2.2s ease-out infinite;
    transform-origin: center;
    transform-box: fill-box;
  }

  @keyframes markerPulse {
    0% { transform: scale(0.5); opacity: 0.8; }
    100% { transform: scale(3); opacity: 0; }
  }
`;

const MapTooltip = styled.div`
  position: absolute;
  top: 1.25rem;
  left: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 999px;
  background: ${props => props.theme.colors.dark};
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  font-weight: 500;
  pointer-events: none;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.theme.colors.primary};
  }
`;

const CountryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const CountryChip = styled.button`
  padding: 0.55rem 1.2rem;
  border-radius: 999px;
  border: 1px solid ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.border)};
  background: ${props => (props.$active ? `${props.theme.colors.primary}18` : 'transparent')};
  color: ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.gray)};
  font-size: 0.9rem;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.25s ease;
  cursor: default;
`;

const TrustedBy = styled.div`
  margin-top: 5rem;

  .trusted-label {
    text-align: center;
    color: ${props => props.theme.colors.gray};
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-bottom: 2.5rem;
  }
`;

const ClientsWall = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  border-top: 1px solid ${props => props.theme.colors.border};
  border-left: 1px solid ${props => props.theme.colors.border};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ClientCell = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-height: 110px;
  padding: 1.5rem 1rem;
  border-right: 1px solid ${props => props.theme.colors.border};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  text-align: center;
  transition: background 0.3s ease;

  .client-name {
    font-family: ${props => props.theme.fonts.heading};
    font-size: 1.15rem;
    font-weight: 600;
    color: ${props => props.theme.colors.gray};
    transition: color 0.3s ease;
  }

  .client-industry {
    font-size: 0.8rem;
    color: ${props => props.theme.colors.gray}99;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  &:hover {
    background: ${props => props.theme.colors.primary}0d;

    .client-name {
      color: ${props => props.theme.colors.text};
    }
  }
`;

const clientLocations = [
  { name: 'USA', coordinates: [-98, 39] },
  { name: 'Canada', coordinates: [-106, 56] },
  { name: 'Mexico', coordinates: [-102, 23.6] },
  { name: 'Argentina', coordinates: [-64, -34.6] },
  { name: 'Netherlands', coordinates: [5.3, 52.2] },
  { name: 'Germany', coordinates: [10.4, 51.2] },
  { name: 'Italy', coordinates: [12.5, 42.8] },
  { name: 'Armenia', coordinates: [45, 40.2] },
  { name: 'Israel', coordinates: [34.9, 31.4] },
  { name: 'UAE', coordinates: [54.4, 23.9] },
];

const clients = [
  { name: 'Takeda', industry: 'Pharma' },
  { name: 'Mastercard', industry: 'Payments' },
  { name: 'Adidas', industry: 'Retail' },
  { name: 'Wolters Kluwer', industry: 'Information' },
  { name: 'IHG', industry: 'Hospitality' },
  { name: 'Verifone', industry: 'Fintech' },
  { name: 'Amplifon', industry: 'Healthcare' },
  { name: 'Verope', industry: 'Manufacturing' },
  { name: 'HeadSense', industry: 'MedTech' },
  { name: 'Synergy', industry: 'GovTech' },
];

const Clients = () => {
  const [hovered, setHovered] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const wallVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.06 },
    },
  };

  const cellVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <ClientsSection id="clients">
      <Container ref={ref}>
        <SectionTitle>
          Clients Across <span className="highlight">10 Countries</span>
        </SectionTitle>
        <SectionSubtitle>
          From startups to global enterprises — delivering hands-on consulting
          and engineering across four continents.
        </SectionSubtitle>

        <MapWrapper
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{ scale: 175, center: [10, 12] }}
            width={980}
            height={460}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies
                  .filter(geo => geo.properties.name !== 'Antarctica')
                  .map(geo => (
                    <Geography key={geo.rsmKey} geography={geo} className="geo" />
                  ))
              }
            </Geographies>
            {clientLocations.map(({ name, coordinates }) => (
              <Marker
                key={name}
                coordinates={coordinates}
                onMouseEnter={() => setHovered(name)}
                onMouseLeave={() => setHovered(null)}
              >
                <circle className="marker-pulse" r={5} />
                <circle className="marker-dot" r={hovered === name ? 6.5 : 4.5} style={{ transition: 'r 0.2s ease' }} />
              </Marker>
            ))}
          </ComposableMap>
          {hovered && (
            <MapTooltip>
              <span className="dot" />
              {hovered}
            </MapTooltip>
          )}
        </MapWrapper>

        <CountryChips>
          {clientLocations.map(({ name }) => (
            <CountryChip
              key={name}
              type="button"
              $active={hovered === name}
              onMouseEnter={() => setHovered(name)}
              onMouseLeave={() => setHovered(null)}
            >
              {name}
            </CountryChip>
          ))}
        </CountryChips>

        <TrustedBy>
          <p className="trusted-label">Trusted by teams at</p>
          <ClientsWall
            variants={wallVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            {clients.map(client => (
              <ClientCell key={client.name} variants={cellVariants}>
                <span className="client-name">{client.name}</span>
                <span className="client-industry">{client.industry}</span>
              </ClientCell>
            ))}
          </ClientsWall>
        </TrustedBy>
      </Container>
    </ClientsSection>
  );
};

export default Clients;
